import { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  useTheme,
  alpha,
  Modal,
  Divider,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import { useAppSelector } from '../../store/hooks';
import { selectMemberSession } from '../../store/slices/onboardingSlice';
import { Logger } from '../../config/logging/logger';
import { submitCoachFeedback, CoachFeedbackRating } from '../../api/coachAPI';
import OptimusButton from '../../common/library/Button/Button';
import OptimusInput from '../../common/library/Input/Input';
import { useCustomEventTracking } from '../../common/useCustomEventTracking';
import { TestIds } from '../../assets/testIds';

const logger = Logger.create('SessionFeedback');

type FeedbackType = CoachFeedbackRating | null;

interface SessionFeedbackProps {
  conversationId: string;
  // The rated turn's agent run id - feedback is keyed on it, so the thumbs are
  // only rendered once it is known.
  runId: string;
  // The gateway's id for the rated answer. Its feedback API requires it, so it
  // is required here too: without it nothing could be recorded and the thumbs
  // would be a dead control.
  assistantMessageId: string;
  agentId: string;
  isSubmitted: boolean;
  /**
   * The rating already recorded against this turn, when there is one. Reopening a
   * conversation remounts this component with no memory of the click that made
   * the rating, so the thumb the member actually chose has to be told to it -
   * otherwise a rated answer comes back thanking them with neither thumb filled.
   */
  submittedRating?: CoachFeedbackRating | null;
  onFeedbackSubmitted: (runId: string, rating: CoachFeedbackRating) => void;
}

export function SessionFeedback({
  conversationId,
  runId,
  assistantMessageId,
  agentId,
  isSubmitted,
  submittedRating,
  onFeedbackSubmitted,
}: SessionFeedbackProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const { memberId } = useAppSelector(selectMemberSession);
  const { trackEngagement } = useCustomEventTracking();
  const [selectedFeedback, setSelectedFeedback] = useState<FeedbackType>(null);
  // Which thumb is drawn filled: the one already on record for this turn, or the
  // one being given right now. Reading the stored rating straight from the prop
  // keeps it correct when a late `listCoachFeedback` response arrives after this
  // component has mounted, which copying it into state on mount would not.
  const shownFeedback = submittedRating ?? selectedFeedback;
  const [showModal, setShowModal] = useState(false);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showError, setShowError] = useState(false);

  // Properties shared by the thumb and comment events so a rating can be tied
  // back in Heap to the conversation and message it was given on.
  const feedbackEventDetail = {
    conversationId,
    ...(assistantMessageId && { assistantMessageId }),
    ...(agentId && { agentId }),
    ...(runId && { runId }),
  };

  // The same ids submitFeedback needs before it can record anything. Feedback is
  // also offered on a turn that errored, which never received an
  // assistantMessageId: a thumb there saves nothing, so it must not be reported
  // either - it would count engagement that no stored feedback backs up.
  const canRecordFeedback = !!(memberId && assistantMessageId && agentId);

  const handleThumbClick = (type: CoachFeedbackRating) => {
    if (isSubmitted) return;
    if (canRecordFeedback) {
      trackEngagement(
        'Coaching',
        type === CoachFeedbackRating.Like ? 'Like' : 'Dislike',
        'AiCoachResponse',
        feedbackEventDetail,
      );
    }
    setSelectedFeedback(type);
    setShowModal(true);
    setComment('');
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setComment('');
  };

  const submitFeedback = async (includeComment: boolean) => {
    if (!selectedFeedback || !memberId || !runId || !assistantMessageId || !agentId) {
      logger.warn('Missing required feedback data');
      handleCloseModal();
      return;
    }

    const trimmedComment = comment.trim();

    // Report the comment on the Submit click itself, so the event reflects the
    // member's action rather than whether the save round-tripped. Cancelling
    // records the thumb on its own, so it reports no comment. The comment text
    // is left out of analytics (it is stored with the feedback) - only its
    // length is reported.
    if (includeComment && trimmedComment) {
      trackEngagement(
        'Coaching',
        'Submit',
        selectedFeedback === CoachFeedbackRating.Like ?
          'AiCoachLikeComment' :
          'AiCoachDislikeComment',
        { ...feedbackEventDetail, commentLength: trimmedComment.length },
      );
    }

    setIsSubmitting(true);

    try {
      await submitCoachFeedback({
        conversationId,
        runId,
        assistantMessageId,
        agentId,
        rating: selectedFeedback,
        ...(includeComment && trimmedComment && { text: trimmedComment }),
      });
      onFeedbackSubmitted(runId, selectedFeedback);
      handleCloseModal();
    } catch (error) {
      logger.error(Logger.format('Failed to submit feedback', error));
      // Close the modal but surface the failure so the user knows it
      // wasn't recorded and can retry. Reset the selection so the thumb
      // returns to its normal (unselected) state instead of staying filled.
      handleCloseModal();
      setSelectedFeedback(null);
      setShowError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = () => submitFeedback(true);

  // Cancelling still records the thumbs up/down rating, just without any text.
  const handleCancel = () => submitFeedback(false);

  const placeholderText = selectedFeedback === CoachFeedbackRating.Like ?
    t('coach.feedback.placeholderPositive') :
    t('coach.feedback.placeholderNegative');

  // Figma "button/medium" typography shared by both dialog actions.
  const actionTypography = {
    minWidth: 'auto',
    height: 'auto',
    borderRadius: theme.spacing(0.5),
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '24px',
    letterSpacing: '0.4px',
  };

  // Submit: filled primary button with MUI elevation/2 shadow.
  const submitButtonSx = {
    ...actionTypography,
    padding: theme.spacing(0.75, 2),
    boxShadow:
      '0px 3px 1px -2px rgba(0, 0, 0, 0.2), ' +
      '0px 2px 2px 0px rgba(0, 0, 0, 0.14), ' +
      '0px 1px 5px 0px rgba(0, 0, 0, 0.12)',
  };

  return (
    <>
      <Box
        data-testid={TestIds.coach.feedbackBar}
        sx={{
          display: 'flex',
          // In a narrow chat column there is no room for the thank-you beside the
          // label and the thumbs, so it drops to a second line instead of
          // squeezing the three of them onto one.
          flexWrap: 'wrap',
          gap: theme.spacing(1.625),
          alignItems: 'center',
          // The message bubble lets long words break mid-token so a pasted link
          // cannot escape it. That is inherited, and this bar is chrome rather
          // than member text: its words break at spaces like normal prose.
          overflowWrap: 'normal',
          // The design sets the whole row apart from the answer above it with a
          // tinted, outlined container in the MUI primary blue.
          padding: `${theme.spacing(1)} ${theme.spacing(1.5)}`,
          borderRadius: theme.spacing(1),
          backgroundColor: alpha(theme.palette.primary.main, 0.08),
          border: `1px solid ${alpha(theme.palette.primary.main, 0.5)}`,
        }}
      >
        <Typography
          variant="bodyMdProminent"
          data-testid={TestIds.coach.feedbackLabel}
          sx={{
            color: theme.palette.text.primary,
            opacity: isSubmitted ? 0.5 : 1,
            // The design keeps the label on one line beside the thumbs. Left to
            // shrink it wraps, and a label reading "Response / feedback:" - or
            // worse, mid-word - is not what the design draws.
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}
        >
          {t('coach.feedback.responseLabel')}
        </Typography>
        <IconButton
          size="small"
          data-testid={TestIds.coach.feedbackThumbsUpBtn}
          onClick={() => handleThumbClick(CoachFeedbackRating.Like)}
          disabled={isSubmitted}
          sx={{
            padding: 0,
            width: theme.spacing(3),
            height: theme.spacing(3),
            flexShrink: 0,
          }}
        >
          {shownFeedback === CoachFeedbackRating.Like ? (
            <ThumbUpIcon
              sx={{
                fontSize: theme.spacing(3),
              }}
            />
          ) : (
            <ThumbUpOutlinedIcon
              sx={{
                fontSize: theme.spacing(3),
                color: theme.palette.grey[800],
              }}
            />
          )}
        </IconButton>
        <IconButton
          size="small"
          data-testid={TestIds.coach.feedbackThumbsDownBtn}
          onClick={() => handleThumbClick(CoachFeedbackRating.Dislike)}
          disabled={isSubmitted}
          sx={{
            padding: 0,
            width: theme.spacing(3),
            height: theme.spacing(3),
            flexShrink: 0,
          }}
        >
          {shownFeedback === CoachFeedbackRating.Dislike ? (
            <ThumbDownIcon
              sx={{
                fontSize: theme.spacing(3),
              }}
            />
          ) : (
            <ThumbDownOutlinedIcon
              sx={{
                fontSize: theme.spacing(3),
                color: theme.palette.grey[800],
              }}
            />
          )}
        </IconButton>
        {isSubmitted && (
          <Typography
            variant="bodyMd"
            sx={{
              color: theme.palette.grey[800],
            }}
          >
            {t('coach.feedback.thankYou')}
          </Typography>
        )}
      </Box>

      <Modal
        open={showModal}
        onClose={() => {
          if (isSubmitting) return;
          handleCancel();
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            backgroundColor: theme.palette.common.white,
            borderRadius: theme.spacing(0.5),
            width: theme.spacing(55.5),
            maxWidth: '90vw',
            boxShadow:
              '0px 8px 10px -5px rgba(0, 0, 0, 0.2), ' +
              '0px 16px 24px 2px rgba(0, 0, 0, 0.14), ' +
              '0px 6px 30px 5px rgba(0, 0, 0, 0.12)',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Title */}
          <Box
            sx={{
              padding: `${theme.spacing(2)} ${theme.spacing(3)}`,
            }}
          >
            <Typography
              sx={{
                fontSize: theme.spacing(2.5),
                fontWeight: 500,
                lineHeight: 1.6,
                letterSpacing: theme.spacing(0.01875),
                color: 'rgba(0, 0, 0, 0.87)',
              }}
            >
              {t('coach.feedback.modalTitle')}
            </Typography>
          </Box>

          <Divider />

          {/* Content */}
          <Box
            sx={{
              padding: theme.spacing(3),
            }}
          >
            <OptimusInput
              data-testid={TestIds.coach.feedbackCommentInput}
              label={t('coach.feedback.label') || ''}
              placeholderText={placeholderText}
              value={comment}
              onInputChange={(e) => setComment(e.target.value)}
              width="100%"
              multiline
              rows={4}
              autoFocus
            />
          </Box>

          <Divider />

          {/* Actions */}
          <Box
            sx={{
              display: 'flex',
              gap: theme.spacing(1),
              justifyContent: 'flex-end',
              padding: theme.spacing(1),
            }}
          >
            <OptimusButton
              data-testid={TestIds.coach.feedbackCancelBtn}
              customVariant="secondaryGray"
              buttonText={t('button.cancel') || ''}
              onClick={handleCancel}
              disabled={isSubmitting}
              fullWidth={false}
              sx={{ ...actionTypography, padding: theme.spacing(0.75, 2) }}
            />
            <OptimusButton
              data-testid={TestIds.coach.feedbackSubmitBtn}
              customVariant="primary"
              buttonText={isSubmitting ?
                <CircularProgress color="inherit" size={24} /> :
                t('button.submit') || ''}
              onClick={handleSubmit}
              disabled={isSubmitting}
              fullWidth={false}
              sx={submitButtonSx}
            />
          </Box>
        </Box>
      </Modal>

      <Snackbar
        open={showError}
        autoHideDuration={4000}
        onClose={() => setShowError(false)}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Alert
          severity="error"
          onClose={() => setShowError(false)}
          sx={{ width: '100%' }}
        >
          {t('coach.feedback.errorMessage')}
        </Alert>
      </Snackbar>
    </>
  );
}
